import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import { TemplatedConfigurationRunDrawer } from "#src/batteries-included-components/Drawers/TemplatedConfigurationRunDrawer";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useListTemplatedConfigurationsRun } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import type { ConfigurationTemplateRunsFiltersType } from "#src/routes/data-tools/submissions/configuration-templates/ConfigurationTemplateRunsFilterPanel";
import { ExceptionUtils } from "#src/utils/exception";
import { linkToAssetDetailPage } from "#utils/links";
import {
  useMutation,
  useQueries,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  ButtonWithPopover,
  DataTable,
  DataTablePanel,
  HeaderType,
  Link,
  Pill,
  PillProps,
  PillToggleGroup,
  PillToggleVariants,
  Skeleton,
  StorageKeys,
  useToast,
  type PillToggleProps,
} from "@validereinc/common-components";
import {
  AssetTypeType,
  ResourceDefinitions,
  Resources,
  TemplatedConfigurationRun,
  TemplatedConfigurationRunAdapter,
  TemplatedConfigurationRunCreatedSchema,
  TemplatedConfigurationRunStatus,
  UsersAdapter,
  UserReturnType,
  type TemplatedConfigurationRunCreatedType,
  type TemplatedConfigurationRunResource,
  type TemplatedConfigurationRunStatusType,
} from "@validereinc/domain";
import {
  BaseError,
  downloadLink,
  toTitleCaseString,
} from "@validereinc/utilities";
import endOfDay from "date-fns/endOfDay";
import parseISO from "date-fns/parseISO";
import startOfDay from "date-fns/startOfDay";
import React, { useMemo, useState } from "react";
import { ConfigurationTemplateSubmissionsRoutePath } from ".";

export const ConfigurationTemplateRunsTablePanel = ({
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { localize } = useLocalization();
  const { toast } = useToast();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [shownRunDetails, setShownRunDetails] = useState<Pick<
    TemplatedConfigurationRunCreatedType,
    "id" | "templated_configuration_name"
  > | null>(null);
  const [filters, setFilters] =
    useStickyState<ConfigurationTemplateRunsFiltersType>(
      {},
      viewConfigStorageKey
    );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: TemplatedConfigurationRun.keyof().Enum.created_at,
      sortDirection: "desc",
    },
    filters,
    tableStateStorageKey
  );
  const exportRunsMutation = useMutation({
    mutationFn: async (
      payload: Parameters<typeof TemplatedConfigurationRunAdapter.exportList>[0]
    ) => {
      if (!payload.meta.templated_configuration_name) return;

      const reportData =
        await TemplatedConfigurationRunAdapter.exportList(payload);

      if (!reportData.s3_download_link || !reportData.name) {
        throw new BaseError("Export does not have a download link.", {
          cause: reportData,
        });
      }

      downloadLink(reportData.s3_download_link, reportData.name);
      return reportData;
    },
    onSuccess: (exportData) => {
      toast.push({
        intent: "success",
        description: `Successfully exported submissions as: ${exportData!.name}`,
      });
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export submissions.",
      });
    },
  });

  const { created_at, ...restFilters } = filters;

  const configRunsQuery = useListTemplatedConfigurationsRun({
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...restFilters,
      ...((created_at?.from && created_at?.to
        ? {
            $and: [
              {
                created_at: {
                  $gte: startOfDay(parseISO(created_at.from)),
                },
              },
              {
                created_at: { $lte: endOfDay(parseISO(created_at.to)) },
              },
            ],
          }
        : {}) as any),
    },
  });

  const primaryAssetsMap = useMemo(() => {
    return (
      configRunsQuery.data?.data?.reduce<
        Record<string, TemplatedConfigurationRunResource>
      >((acc, run) => {
        const primaryResource = run?.resources?.find(
          (resource) => resource.is_primary
        );

        if (primaryResource) {
          acc[run?.id] = primaryResource;
        }

        return acc;
      }, {}) ?? {}
    );
  }, [configRunsQuery.data?.data.map((d) => d.id)]);

  const configRunsMetaUsersQueries = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries:
      Array.from(
        configRunsQuery.data?.data?.reduce((userIdsToFetch, run) => {
          if (run.created_by) {
            userIdsToFetch.add(run.created_by);
          }

          if (run.updated_by) {
            userIdsToFetch.add(run.updated_by);
          }

          return userIdsToFetch;
        }, new Set<string>()) ?? new Set<string>()
      ).map((userId) => ({
        queryKey: ["users", userId],
        queryFn: () => {
          if (!userId) return;

          return UsersAdapter.getOne({
            id: userId,
          });
        },
        enabled: Boolean(userId),
        staleTime: 3 * 60 * 1000,
        select: (resp) => resp?.data,
      })) ?? [],
  });

  const configRunsMetaUsersMap = useMemo(() => {
    return configRunsMetaUsersQueries.reduce<Record<string, UserReturnType>>(
      (userMap, q) => {
        if (!q.data?.id || userMap[q.data.id]) {
          return userMap;
        }

        userMap[q.data.id] = q.data;
        return userMap;
      },
      {}
    );
  }, [configRunsMetaUsersQueries]);

  const {
    data: templatedConfigurationRunQuery,
    isLoading: isTemplatedConfigurationRunLoading,
  } = useQuery({
    queryKey: [
      Resources.TEMPLATED_CONFIGURATION_RUN,
      shownRunDetails?.templated_configuration_name,
      shownRunDetails?.id,
    ],
    queryFn: () => {
      if (
        !shownRunDetails?.templated_configuration_name ||
        !shownRunDetails?.id
      )
        return null;

      return TemplatedConfigurationRunAdapter.getOne({
        id: shownRunDetails.id,
        meta: {
          templated_configuration_name:
            shownRunDetails.templated_configuration_name,
        },
      });
    },
    enabled:
      !!shownRunDetails?.templated_configuration_name && !!shownRunDetails.id,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: templatedConfigurationRunDependencyGraphQuery,
    isLoading: isTemplatedConfigurationRunDependencyGraphLoading,
  } = useQuery({
    queryKey: [
      Resources.TEMPLATED_CONFIGURATION_RUN,
      "dependencyGraph",
      shownRunDetails?.id,
    ],
    queryFn: () => {
      if (!templatedConfigurationRunQuery?.data?.primary_resource?.id)
        return null;

      return TemplatedConfigurationRunAdapter.dependencyGraph.getOne({
        id: templatedConfigurationRunQuery.data.primary_resource.id,
      });
    },
    enabled: !!templatedConfigurationRunQuery?.data?.primary_resource?.id,
    staleTime: 5 * 60 * 1000,
  });

  const templatedConfigurationRunDependencyGraph =
    templatedConfigurationRunDependencyGraphQuery?.data;

  const pillToggles: PillToggleProps[] = [
    {
      name: "All",
      label: "All",
      value: "",
      isSelected: !filters.status?.length || filters.status?.includes(""),
      count: configRunsQuery.data?.total_entries ?? 0,
      shouldSelectAll: true,
    },
    {
      name: TemplatedConfigurationRunStatus.SUCCESS,
      label: toTitleCaseString(TemplatedConfigurationRunStatus.SUCCESS, {
        intent: "ui",
      }),
      value: TemplatedConfigurationRunStatus.SUCCESS,
      variant: PillToggleVariants.GOOD,
      isSelected:
        filters.status?.includes(TemplatedConfigurationRunStatus.SUCCESS) ??
        false,
      count:
        configRunsQuery.data?.data.filter(
          (configRun) =>
            configRun.status === TemplatedConfigurationRunStatus.SUCCESS
        ).length ?? 0,
    },
    {
      name: TemplatedConfigurationRunStatus.SUBMITTED,
      label: toTitleCaseString(TemplatedConfigurationRunStatus.SUBMITTED, {
        intent: "ui",
      }),
      value: TemplatedConfigurationRunStatus.SUBMITTED,
      variant: PillToggleVariants.PENDING,
      isSelected:
        filters.status?.includes(TemplatedConfigurationRunStatus.SUBMITTED) ??
        false,
      count:
        configRunsQuery.data?.data.filter(
          (configRun) =>
            configRun.status === TemplatedConfigurationRunStatus.SUBMITTED
        ).length ?? 0,
    },
    {
      name: TemplatedConfigurationRunStatus.PARTIAL,
      label: toTitleCaseString(TemplatedConfigurationRunStatus.PARTIAL, {
        intent: "ui",
      }),
      value: TemplatedConfigurationRunStatus.PARTIAL,
      variant: PillToggleVariants.ATTENTION,
      isSelected:
        filters.status?.includes(TemplatedConfigurationRunStatus.PARTIAL) ??
        false,
      count:
        configRunsQuery.data?.data.filter(
          (configRun) =>
            configRun.status === TemplatedConfigurationRunStatus.PARTIAL
        ).length ?? 0,
    },
    {
      name: TemplatedConfigurationRunStatus.FAILED,
      label: toTitleCaseString(TemplatedConfigurationRunStatus.FAILED, {
        intent: "ui",
      }),
      value: TemplatedConfigurationRunStatus.FAILED,
      variant: PillToggleVariants.FAILURE,
      isSelected:
        filters.status?.includes(TemplatedConfigurationRunStatus.FAILED) ??
        false,
      count:
        configRunsQuery.data?.data.filter(
          (configRun) =>
            configRun.status === TemplatedConfigurationRunStatus.FAILED
        ).length ?? 0,
    },
  ];

  const headers: Array<HeaderType<TemplatedConfigurationRunCreatedType>> = [
    {
      key: TemplatedConfigurationRunCreatedSchema.keyof().Enum
        .templated_configuration_name,
      label: "Configuration Template",
      isSortable: true,
      renderComponent: ({ item }) => {
        const name =
          item?.templated_configuration?.display_name ??
          item?.templated_configuration?.name ??
          item?.templated_configuration_name ??
          null;

        const isDrawerLoading =
          item.id === shownRunDetails?.id &&
          isTemplatedConfigurationRunLoading &&
          isTemplatedConfigurationRunDependencyGraphLoading;

        return name ? (
          <Skeleton isLoading={isDrawerLoading}>
            <div>
              <Link
                onClick={() =>
                  setShownRunDetails({
                    id: item.id,
                    templated_configuration_name:
                      item.templated_configuration_name,
                  })
                }
                label={name}
              />
            </div>
          </Skeleton>
        ) : (
          "-"
        );
      },
    },
    {
      key: `${TemplatedConfigurationRunCreatedSchema.keyof().Enum.templated_configuration}.${
        TemplatedConfigurationRunCreatedSchema.shape.templated_configuration.keyof()
          .Enum.primary_resource_type
      }`,
      label: "Primary Asset Type",
      isSortable: true,
      renderComponent: ({ item }) => {
        return localize(item?.templated_configuration?.primary_resource_type);
      },
    },
    {
      key: `${TemplatedConfigurationRunCreatedSchema.keyof().Enum.resources}.${TemplatedConfigurationRunCreatedSchema.shape.resources.element.keyof().Enum.name}`,
      label: "Primary Asset",
      isSortable: true,
      renderComponent: ({ item }) => {
        if (!primaryAssetsMap[item.id]) return "-";

        const primaryAsset = primaryAssetsMap[item.id];

        if (primaryAsset.id && primaryAsset.type && primaryAsset.name) {
          return (
            <RoutingLink
              to={linkToAssetDetailPage(
                primaryAsset.type as AssetTypeType,
                primaryAsset.id
              )}
            >
              {primaryAssetsMap[item.id].name}
            </RoutingLink>
          );
        } else if (primaryAsset.name) {
          return primaryAsset.name;
        } else {
          return "-";
        }
      },
    },
    {
      key: TemplatedConfigurationRunCreatedSchema.keyof().Enum.status,
      label: "Status",
      isSortable: true,
      renderComponent: ({ item }) => (
        <Pill
          variant={
            (
              {
                started: "info",
                partial: "warning",
                failed: "error",
                submitted: "info",
                success: "success",
              } satisfies Record<
                (typeof TemplatedConfigurationRunStatus)[keyof typeof TemplatedConfigurationRunStatus],
                PillProps["variant"]
              >
            )[item?.status]
          }
        >
          {toTitleCaseString(item?.status ?? "-", {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      key: TemplatedConfigurationRunCreatedSchema.keyof().Enum.created_at,
      label: "Created On",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item?.created_at}
          withTime
        />
      ),
    },
    {
      key: TemplatedConfigurationRunCreatedSchema.keyof().Enum.created_by,
      label: "Created By",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.created_by ? (
          <UserDataDisplay
            id={item.created_by}
            name={configRunsMetaUsersMap[item.created_by]?.name ?? "n/a"}
          />
        ) : (
          "-"
        ),
    },
  ];

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: ConfigurationTemplateSubmissionsRoutePath.title,
          titleDecorator: (
            <PillToggleGroup
              name="status"
              pills={pillToggles}
              onChange={(val) =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  status: val as TemplatedConfigurationRunStatusType[],
                }))
              }
            />
          ),
          actionRow: [
            <ButtonWithPopover
              key="export"
              buttonProps={{
                icon: "download-simple",
                variant: "outline",
                disabled: !filters.templated_configuration_name,
                isLoading: exportRunsMutation.isLoading,
                onClick: () => {
                  if (!filters.templated_configuration_name) return;

                  exportRunsMutation.mutate({
                    sortBy: tableState.sortBy,
                    sortDirection: tableState.sortDirection ?? "desc",
                    filters: {
                      ...(filters.status ? { status: filters.status } : {}),
                      ...((created_at?.from && created_at?.to
                        ? {
                            $and: [
                              {
                                created_at: {
                                  $gte: startOfDay(parseISO(created_at.from)),
                                },
                              },
                              {
                                created_at: {
                                  $lte: endOfDay(parseISO(created_at.to)),
                                },
                              },
                            ],
                          }
                        : {}) as any),
                      isAlreadyFormatted: true,
                    },
                    meta: {
                      templated_configuration_name:
                        filters.templated_configuration_name,
                    },
                  });
                },
              }}
              label="Export"
            />,
            ...(isDataIngestionEnabled && filters.templated_configuration_name
              ? [
                  <ImportDataAction
                    key="import"
                    resource={ResourceDefinitions.equipment}
                    preselectedTemplatedConfigurationName={
                      filters.templated_configuration_name
                    }
                    modes={["templated-configuration"]}
                  />,
                ]
              : []),
          ],
        }}
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items: configRunsQuery.data?.data ?? [],
          getItemId: (item) => item.id,
          isLoading:
            configRunsQuery.isLoading ||
            configRunsMetaUsersQueries.some((q) => q.isLoading),
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: configRunsQuery.data?.total_entries ?? tableState.total,
          },
        }}
      />
      <TemplatedConfigurationRunDrawer
        isOpen={!!templatedConfigurationRunDependencyGraph}
        onClose={() => setShownRunDetails(null)}
        templatedConfigurationRunDependencyGraph={
          templatedConfigurationRunDependencyGraph
        }
        resourceId={""}
      />
    </>
  );
};
